import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];

/**
 * Idle-timeout tracker backing IdleLogoutGate.js — web only (the native app gets its own
 * re-auth via BiometricLockGate.js's Face ID/Touch ID relock; force-logging out on idle
 * there too would be redundant on top of it). Common practice for financial platforms,
 * previously missing entirely: with no idle detection, a silently-refreshed session token
 * meant a tab left open stayed logged in indefinitely.
 *
 * Once the warning fires, only stayLoggedIn() can reset the timer — background page
 * activity is ignored while the warning's blocking overlay is up, since anything the user
 * does at that point should go through its own "stay logged in" action, not incidentally
 * dismiss a security prompt via an unrelated mousemove.
 */
export default function useIdleTimer({ idleMs, warningMs, onTimeout, enabled = true }) {
  const [warning, setWarningState] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(warningMs / 1000));
  const warningRef = useRef(false);
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tickRef = useRef(null);

  const setWarning = useCallback((value) => {
    warningRef.current = value;
    setWarningState(value);
  }, []);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(tickRef.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    clearAllTimers();
    setWarning(false);
    warnTimerRef.current = setTimeout(() => {
      setWarning(true);
      const deadline = Date.now() + warningMs;
      setSecondsRemaining(Math.ceil(warningMs / 1000));
      tickRef.current = setInterval(() => {
        setSecondsRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
      }, 1000);
      logoutTimerRef.current = setTimeout(() => {
        clearAllTimers();
        onTimeout();
      }, warningMs);
    }, idleMs);
  }, [clearAllTimers, idleMs, warningMs, onTimeout, setWarning]);

  useEffect(() => {
    if (!enabled) return undefined;
    resetIdleTimer();
    function handleActivity() {
      if (warningRef.current) return;
      resetIdleTimer();
    }
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivity));
    return () => {
      clearAllTimers();
      setWarning(false);
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
    };
    // Deliberately only [enabled]: resetIdleTimer's identity can change across renders
    // (it depends on the caller's onTimeout), and re-running this effect on every such
    // change would tear down and restart the idle clock every render — defeating the
    // point of measuring inactivity across renders. The listener always calls the latest
    // resetIdleTimer via closure at the time it fires regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { warning, secondsRemaining, stayLoggedIn: resetIdleTimer };
}
